import type { MenuItem } from '../components/Menu/Menu';
import { getMenuLabels, type MenuLocale } from './menuLabels';

const DEFAULT_MENU_LOCALE: MenuLocale = 'pt';

export const getMenuLocale = (): MenuLocale => {
  if (typeof document === 'undefined') {
    return DEFAULT_MENU_LOCALE;
  }

  const lang = document.documentElement.lang.split('-')[0] as MenuLocale;
  return lang || DEFAULT_MENU_LOCALE;
};

const filterMenuItems = (items: MenuItem[], itemIds?: string[]): MenuItem[] => {
  if (!itemIds || itemIds.length === 0) {
    return items;
  }

  const allowedIds = new Set(itemIds);

  return items.reduce<MenuItem[]>((accumulator, item) => {
    const children = item.children ? filterMenuItems(item.children, itemIds) : [];
    const shouldInclude = allowedIds.has(item.id) || children.length > 0;

    if (shouldInclude) {
      accumulator.push({
        ...item,
        children,
      });
    }

    return accumulator;
  }, []);
};

export const menuPaths = {
  brandHealth: '/dashboard/saude-de-marca',
  postsWall: '/dashboard/publicacoes/mural',
  visualMedia: '/dashboard/publicacoes/midiasvisuais',
  influencerRt: '/dashboard/publicacoes/mural/rts-de-influenciadores',
  classify: '/dashboard/publicacoes/classificar',
  files: '/dashboard/publicacoes/arquivos',
  exportsCenter: '/dashboard/publicacoes/exportar',
  pressWall: '/dashboard/publicacoes/mural/imprensa',
  summaryMainPress: '/dashboard/resumo/imprensa',
  overviewSummary: '/dashboard/resumo/visaogeral',
  overviewStats: '/dashboard/resumo/estatisticas',
  discoveriesWhatMatrix: '/dashboard/descobrir/oque/matriz-comparativa',
  discoveriesWhatRanking: '/dashboard/descobrir/oque/ranking-evolucao',
  discoveriesWhatTermsTime: '/dashboard/descobrir/oque/termos-no-tempo',
  discoveriesWhatCorrelatedTerms: '/dashboard/descobrir/oque/termos-correlacionados',
  discoveriesWhatTermsDuel: '/dashboard/descobrir/oque/duelo',
  discoveriesWhoRankingAaa: '/dashboard/descobrir/quem/ranking-aaa',
  discoveriesWhoCompare: '/dashboard/descobrir/quem/comparar-influenciadores',
  discoveriesWhoInterests: '/dashboard/descobrir/quem/principais-interesses',
  discoveriesWhoNetwork: '/dashboard/descobrir/quem/rede-de-influenciadores',
  discoveriesWhoInteractions: '/dashboard/descobrir/quem/rede-de-interacoes',
  discoveriesWhoTerms: '/dashboard/descobrir/quem/influenciadores-vs-termos',
  discoveriesWhere: '/dashboard/descobrir/onde',
  discoveriesWhen: '/dashboard/descobrir/quando',
  boardsThemesTopics: '/dashboard/live/temas-e-topicos',
  boardsLiveMentions: '/dashboard/live/mencoes-ao-vivo',
  boardsEvolution: '/dashboard/live/evolucao',
  boardsWhoSpeaks: '/dashboard/live/ranking-aaa',
  boardsWhereSpeaks: '/dashboard/live/onde-falam',
  boardsSocialHeadlines: '/dashboard/live/manchetes-sociais',
  benchmarks: '/benchmarks',
  performance: '/metricas-proprietarias',
  publisher: '/scheduled-content/list',
  sac: '/conversas',
  studio: '/dashboard/stilingue-studio',
  alertsCreate: '/dashboard/alertas/criar',
  alertsView: '/dashboard/alertas/ver',
  automation: '/automacao',
} as const;

export const getMenuItems = (
  _locale: MenuLocale,
  itemIds?: string[],
  accountID?: string,
  universeID?: string,
): MenuItem[] => {
  const labels = getMenuLabels(_locale);

  const getEditSearchPath = () => {
    if (accountID && universeID) {
      return `/editarpesquisa/${accountID}/${universeID}`;
    }
    return '/editarpesquisa';
  };

  const items: MenuItem[] = [
    {
      id: 'brand-health',
      label: labels.brandHealth,
      icon: 'heart',
      path: menuPaths.brandHealth,
      href: menuPaths.brandHealth,
    },
    {
      id: 'posts',
      label: labels.posts,
      icon: 'like',
      children: [
        {
          id: 'posts-wall',
          label: labels.postsWall,
          path: menuPaths.postsWall,
          href: menuPaths.postsWall,
        },
        {
          id: 'visual-media',
          label: labels.visualMedia,
          path: menuPaths.visualMedia,
          href: menuPaths.visualMedia,
        },
        {
          id: 'influencer-rt',
          label: labels.influencerRt,
          path: menuPaths.influencerRt,
          href: menuPaths.influencerRt,
        },
        {
          id: 'classify',
          label: labels.classify,
          path: menuPaths.classify,
          href: menuPaths.classify,
        },
        {
          id: 'files',
          label: labels.files,
          path: menuPaths.files,
          href: menuPaths.files,
        },
        {
          id: 'exports-center',
          label: labels.exportsCenter,
          path: menuPaths.exportsCenter,
          href: menuPaths.exportsCenter,
        },
      ],
    },
    {
      id: 'press',
      label: labels.press,
      icon: 'site',
      children: [
        {
          id: 'press-wall',
          label: labels.pressWall,
          path: menuPaths.pressWall,
          href: menuPaths.pressWall,
        },
        {
          id: 'summary-main-press',
          label: labels.summaryMainPress,
          path: menuPaths.summaryMainPress,
          href: menuPaths.summaryMainPress,
        },
      ],
    },
    {
      id: 'overview',
      label: labels.overview,
      icon: 'chart-line',
      children: [
        {
          id: 'overview-summary',
          label: labels.overviewSummary,
          path: menuPaths.overviewSummary,
          href: menuPaths.overviewSummary,
        },
        {
          id: 'overview-stats',
          label: labels.overviewStats,
          path: menuPaths.overviewStats,
          href: menuPaths.overviewStats,
        },
      ],
    },
    {
      id: 'discover',
      label: labels.discover,
      icon: 'blip-ideas',
      children: [
        {
          id: 'discoveries-what',
          label: labels.discoveriesWhat,
          children: [
            {
              id: 'discoveries-what-matrix',
              label: labels.discoveriesWhatComparativeMatrix,
              path: menuPaths.discoveriesWhatMatrix,
              href: menuPaths.discoveriesWhatMatrix,
            },
            {
              id: 'discoveries-what-ranking',
              label: labels.discoveriesWhatRankingEvolution,
              path: menuPaths.discoveriesWhatRanking,
              href: menuPaths.discoveriesWhatRanking,
            },
            {
              id: 'discoveries-what-terms-time',
              label: labels.discoveriesWhatTermsOverTime,
              path: menuPaths.discoveriesWhatTermsTime,
              href: menuPaths.discoveriesWhatTermsTime,
            },
            {
              id: 'discoveries-what-correlated-terms',
              label: labels.discoveriesWhatCorrelatedTerms,
              path: menuPaths.discoveriesWhatCorrelatedTerms,
              href: menuPaths.discoveriesWhatCorrelatedTerms,
            },
            {
              id: 'discoveries-what-terms-duel',
              label: labels.discoveriesWhatTermsDuel,
              path: menuPaths.discoveriesWhatTermsDuel,
              href: menuPaths.discoveriesWhatTermsDuel,
            },
          ],
        },
        {
          id: 'discoveries-who',
          label: labels.discoveriesWho,
          children: [
            {
              id: 'discoveries-who-ranking-aaa',
              label: labels.discoveriesWhoRankingAAA,
              path: menuPaths.discoveriesWhoRankingAaa,
              href: menuPaths.discoveriesWhoRankingAaa,
            },
            {
              id: 'discoveries-who-compare',
              label: labels.discoveriesWhoCompareInfluencers,
              path: menuPaths.discoveriesWhoCompare,
              href: menuPaths.discoveriesWhoCompare,
            },
            {
              id: 'discoveries-who-interests',
              label: labels.discoveriesWhoMainInterests,
              path: menuPaths.discoveriesWhoInterests,
              href: menuPaths.discoveriesWhoInterests,
            },
            {
              id: 'discoveries-who-network',
              label: labels.discoveriesWhoInfluencersNetwork,
              path: menuPaths.discoveriesWhoNetwork,
              href: menuPaths.discoveriesWhoNetwork,
            },
            {
              id: 'discoveries-who-interactions',
              label: labels.discoveriesWhoInteractionsNetwork,
              path: menuPaths.discoveriesWhoInteractions,
              href: menuPaths.discoveriesWhoInteractions,
            },
            {
              id: 'discoveries-who-terms',
              label: labels.discoveriesWhoInfluencersTerms,
              path: menuPaths.discoveriesWhoTerms,
              href: menuPaths.discoveriesWhoTerms,
            },
          ],
        },
        {
          id: 'discoveries-where',
          label: labels.discoveriesWhere,
          path: menuPaths.discoveriesWhere,
          href: menuPaths.discoveriesWhere,
        },
        {
          id: 'discoveries-when',
          label: labels.discoveriesWhen,
          path: menuPaths.discoveriesWhen,
          href: menuPaths.discoveriesWhen,
        },
      ],
    },
    {
      id: 'live',
      label: labels.live,
      icon: 'catalog',
      children: [
        {
          id: 'boards-themes-topics',
          label: labels.boardsThemesTopics,
          path: menuPaths.boardsThemesTopics,
          href: menuPaths.boardsThemesTopics,
          target: '_blank',
        },
        {
          id: 'boards-live-mentions',
          label: labels.boardsLiveMentions,
          path: menuPaths.boardsLiveMentions,
          href: menuPaths.boardsLiveMentions,
          target: '_blank',
        },
        {
          id: 'boards-evolution',
          label: labels.boardsEvolution,
          path: menuPaths.boardsEvolution,
          href: menuPaths.boardsEvolution,
          target: '_blank',
        },
        {
          id: 'boards-who-speaks',
          label: labels.boardsWhoSpeaks,
          path: menuPaths.boardsWhoSpeaks,
          href: menuPaths.boardsWhoSpeaks,
          target: '_blank',
        },
        {
          id: 'boards-where-speaks',
          label: labels.boardsWhereSpeaks,
          path: menuPaths.boardsWhereSpeaks,
          href: menuPaths.boardsWhereSpeaks,
          target: '_blank',
        },
        {
          id: 'boards-social-headlines',
          label: labels.boardsSocialHeadlines,
          path: menuPaths.boardsSocialHeadlines,
          href: menuPaths.boardsSocialHeadlines,
          target: '_blank',
        },
      ],
    },
    {
      id: 'benchmarks',
      icon: 'chart-column',
      label: labels.benchmarks,
      path: menuPaths.benchmarks,
    },
    {
      id: 'performance',
      icon: 'list',
      label: labels.proprietaryMetrics,
      path: menuPaths.performance,
      href: menuPaths.performance,
    },
    {
      id: 'publisher',
      icon: 'calendar',
      label: labels.publishAndSchedule,
      path: menuPaths.publisher,
      href: menuPaths.publisher,
    },
    {
      id: 'sac',
      icon: 'message-total',
      label: labels.sac,
      path: menuPaths.sac,
      href: menuPaths.sac,
    },
    {
      id: 'studio',
      icon: 'clipboard',
      label: labels.studio,
      path: menuPaths.studio,
      href: menuPaths.studio,
    },
    {
      id: 'alert',
      icon: 'speaker',
      label: labels.alerts,
      children: [
        {
          id: 'alerts-create',
          label: labels.alertsCreate,
          path: menuPaths.alertsCreate,
          href: menuPaths.alertsCreate,
        },
        {
          id: 'alerts-view',
          label: labels.alertsView,
          path: menuPaths.alertsView,
          href: menuPaths.alertsView,
        },
      ],
    },
    {
      id: 'automation',
      icon: 'date-time',
      label: labels.automation,
      path: menuPaths.automation,
      href: menuPaths.automation,
    },
    {
      id: 'edit-search',
      icon: 'settings-general',
      label: labels.editSearch,
      path: getEditSearchPath(),
    },
  ];

  return filterMenuItems(items, itemIds);
};

export const getDefaultMenuItems = (itemIds?: string[]): MenuItem[] => getMenuItems(getMenuLocale(), itemIds);
